import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Button, CircularProgress, Alert, Stack, Divider } from '@mui/material';
import useDataFetch, { usePostDataFetch } from '../../utils/useDataFetch';

const TeamInvitations = () => {
  const navigate = useNavigate();
  const { data, error, isLoading, refetch } = useDataFetch({ url: '/teams/invitations' });
  const { data: acceptData, error: acceptError, isLoading: acceptLoading, postData: acceptInvitation } = usePostDataFetch();
  const { data: declineData, error: declineError, isLoading: declineLoading, postData: declineInvitation } = usePostDataFetch();
  const { data: withdrawData, error: withdrawError, isLoading: withdrawLoading, postData: withdrawInvitation } = usePostDataFetch();

  const handleAccept = async (invitationId) => {
    await acceptInvitation({ url: '/teams/invitations/accept', aBody: { invitationId } });
    refetch();
  };

  const handleDecline = async (invitationId) => {
    await declineInvitation({ url: '/teams/invitations/decline', aBody: { invitationId } });
    refetch();
  };

  const handleWithdraw = async (invitationId) => {
    await withdrawInvitation({ url: '/teams/invitations/withdraw', aBody: { invitationId } });
    refetch();
  };

  if (isLoading) {
    return (
      <CircularProgress />
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 920, m: '0 auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Team-Einladungen</Typography>
        <Button variant="outlined" onClick={() => navigate('/teams')}>Zur Teamverwaltung</Button>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {(acceptError || declineError || withdrawError) && <Alert severity="error">{acceptError || declineError || withdrawError}</Alert>}
      {(acceptData?.success || declineData?.success || withdrawData?.success) && (
        <Alert severity="success" sx={{ mb: 1 }}>
          {acceptData?.message || declineData?.message || withdrawData?.message}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Erhaltene Einladungen</Typography>
          <Divider sx={{ mb: 2 }} />
          {data?.received?.length > 0 ? (
            <Stack spacing={2}>
              {data.received.map((invite) => (
                <Box key={invite.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: "12px" }}>
                  <Typography variant="subtitle1" fontWeight="bold">{invite.team_name}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Von: {invite.sender_first_name} {invite.sender_last_name}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>Status: {invite.status}</Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button
                      variant="contained"
                      onClick={() => handleAccept(invite.id)}
                      disabled={acceptLoading}
                    >
                      Annehmen
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleDecline(invite.id)}
                      disabled={declineLoading}
                    >
                      Ablehnen
                    </Button>
                  </Stack>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary">Du hast zurzeit keine offenen Einladungen.</Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Versendete Einladungen</Typography>
          <Divider sx={{ mb: 2 }} />
          {data?.sent?.length > 0 ? (
            <Stack spacing={2}>
              {data.sent.map((invite) => (
                <Box key={invite.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: "12px" }}>
                  <Typography variant="subtitle1" fontWeight="bold">{invite.team_name}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    An: {invite.receiver_first_name} {invite.receiver_last_name}
                  </Typography>
                  <Typography variant="body2">Status: {invite.status}</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleWithdraw(invite.id)}
                      disabled={withdrawLoading}
                    >
                      Zurückziehen
                    </Button>
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary">Du hast aktuell keine offenen Einladungen versendet.</Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TeamInvitations;
